import {
  Area,
  MovieNetworkModel,
  ProgramNetworkModel,
  ProviderNetworkModel,
  SeriesNetworkModel,
  UIElementNetworkModel,
  type ContentDataNetworkModel,
  type IContentData,
  type KtList
} from '@norlys-a-s/norlys-play-kmm';
import { DEFAULT_FAVORITES_LIST } from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';
import {
  ContentCover,
  ContentType,
  UIElementContent,
  UIElementStyle
} from '@smart-tv/typings/content';
import getUnixDate from '@smart-tv/utils/date/get-unix-date';
import { generateUID } from '@smart-tv/utils/uidGenerator';
import { kmm } from './kmm';
import {
  RawUiElementContentItem,
  getContentType as getMappedContentType,
  getCoverType,
  getLayout,
  getStyle
} from './helpers';

const KMM_STYLE_VALUES = new Set<string>(Object.values(UIElementStyle));

const camelize = (str: string): string =>
  str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());

const getUiElementStyle = (
  style: UIElementNetworkModel.StyleNameNetworkModel | null | undefined
): UIElementStyle => {
  const styleName = style?.name;

  if (!styleName) return UIElementStyle.Mixed;

  const remappedStyle = getStyle(styleName);
  if (remappedStyle !== UIElementStyle.ProgramLandscape) {
    return remappedStyle;
  }

  return KMM_STYLE_VALUES.has(styleName)
    ? (styleName as UIElementStyle)
    : UIElementStyle.ProgramLandscape;
};

const getUiElementCoverType = (style: UIElementStyle, contentType: ContentType): ContentCover =>
  getCoverType({ contentType } as RawUiElementContentItem, style);

const getContentType = (item: IContentData): ContentType => {
  const rawItem = {
    contentType: item instanceof SeriesNetworkModel ? ContentType.Series : ContentType.Movies,
    sid: item instanceof ProgramNetworkModel ? String(item.sId) : undefined,
    providerCollectionName:
      item instanceof ProviderNetworkModel ? item.providerCollectionName : undefined
  } as RawUiElementContentItem;

  return getMappedContentType(rawItem);
};

type UiElementContentDataItem =
  | MovieNetworkModel
  | SeriesNetworkModel
  | ProgramNetworkModel
  | ProviderNetworkModel;

const responseMapper = (
  content: ContentDataNetworkModel<KtList<UiElementContentDataItem>>
): UIElementContent => {
  const style = getUiElementStyle(content.uiElementStyleName);
  const layout = getLayout(style);

  const removable = Array.from(content.secondaryActions?.asJsReadonlyArrayView() ?? []).some(
    (action) => action.name.name === 'REMOVE_FROM_STRIPE'
  );

  const uiElementProperties = [
    ...Array.from(content.booleanProperties.asJsReadonlyArrayView()),
    ...Array.from(content.integerProperties.asJsReadonlyArrayView()),
    ...Array.from(content.enumProperties.asJsReadonlyArrayView())
  ];

  const properties: Record<string, boolean> = {};
  for (const property of uiElementProperties) {
    properties[camelize(property.propertyName)] = Boolean(property.propertyValue);
  }

  const items = Array.from(content.contentData?.asJsReadonlyArrayView() ?? []).map((item) => {
    const contentType = getContentType(item);
    const isProgram = item instanceof ProgramNetworkModel;
    const isProvider = item instanceof ProviderNetworkModel;

    const startEpoch = isProgram ? item.startTimeEpochSeconds : null;
    const endEpoch = isProgram ? item.endTimeEpochSeconds : null;
    const trackingGenre = isProgram ? item.trackingGenre : null;

    const startTime = startEpoch ? getUnixDate(startEpoch) : undefined;
    const endTime = endEpoch ? getUnixDate(endEpoch) : undefined;

    const rawYear = 'year' in item ? item.year : undefined;
    const parsedYear =
      typeof rawYear === 'number'
        ? rawYear
        : typeof rawYear === 'string'
        ? Number.parseInt(rawYear, 10)
        : undefined;
    const year =
      parsedYear !== undefined && Number.isFinite(parsedYear)
        ? parsedYear
        : startTime
        ? startTime.getFullYear()
        : undefined;

    return {
      key: generateUID(),
      accessLogo: item.coverIcon?.name?.toLowerCase() as 'rent' | 'subscription' | undefined,
      contentId:
        item instanceof MovieNetworkModel || item instanceof SeriesNetworkModel
          ? item.contentId
          : undefined,
      contentType,
      cover: item.thumbnailImageUrl || '',
      coverType: getUiElementCoverType(style, contentType),
      contentLayout: layout,
      duration: startEpoch && endEpoch ? endEpoch - startEpoch : 0,
      endTime,
      eventId: isProgram ? item.eventId : undefined,
      favorited:
        item instanceof MovieNetworkModel || item instanceof SeriesNetworkModel
          ? item.isCustomerFavorite ?? undefined
          : isProgram
          ? item.isFavorite
          : undefined,
      genre: trackingGenre ? [trackingGenre] : undefined,
      heroImage:
        item instanceof MovieNetworkModel || item instanceof SeriesNetworkModel
          ? item.heroImage || undefined
          : isProgram
          ? item.secondaryThumbnailImageUrl || undefined
          : undefined,
      logo: item.channelImageUrl || undefined,
      recording: isProgram ? item.isRecording : undefined,
      removable,
      providerCollectionName: isProvider ? item.providerCollectionName : undefined,
      providerCollectionId: isProvider ? item.providerCollectionId : undefined,
      sid: isProgram ? item.sId : undefined,
      startTime,
      title: item.title || undefined,
      year,
      providerCount:
        item instanceof MovieNetworkModel || item instanceof SeriesNetworkModel
          ? item.providerCount
          : undefined
    };
  });

  const contentTotalCount = content.totalElements ?? items.length;
  const showAllElementCell =
    content.showAllAtElementCellPosition !== null &&
    content.showAllAtElementCellPosition !== undefined &&
    content.showAllAtElementCellPosition > 0 &&
    content.showAllAtElementCellPosition < contentTotalCount;

  return {
    id: content.uiElementId ?? 0,
    title: content.uiElementTitle || '',
    layout,
    contentTotalCount,
    showAllElementCell: {
      show: showAllElementCell,
      position: content.showAllAtElementCellPosition ?? 0
    },
    items,
    updateNext: content.secondsToNextUpdate ?? 0,
    properties,
    primaryAction: content.primaryAction
      ? {
          actionName: content.primaryAction.name.name,
          actionParameters: Array.from(
            content.primaryAction.actionParameters.asJsReadonlyArrayView()
          ).map((parameter) => ({
            parameterName: parameter.name?.name || '',
            parameterValue: parameter.value
          }))
        }
      : {
          actionName: '',
          actionParameters: []
        }
  };
};

export const getUiElementContent = async (
  uiElementId: number,
  favoritesListId = DEFAULT_FAVORITES_LIST.id,
  area: Area = Area.Frontpage
): Promise<UIElementContent> => {
  const response = await kmm((repo) =>
    repo.api.getUiElementContent(favoritesListId, true, uiElementId, area, null, null, null, null)
  );

  if (!response) {
    throw new Error('getUiElementContent returned empty response from KMM');
  }

  return responseMapper(response as ContentDataNetworkModel<KtList<UiElementContentDataItem>>);
};

export default getUiElementContent;
